import React from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import Modal from 'modal/modal';
import classnames from 'classnames';
import EventBarrier from 'photoswipe/event-barrier/event-barrier';
import Transition, { ENTERED } from 'react-transition-group/Transition';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import close from 'four-nets/icons/svg/icon_close.svg';
import commentsSvg from 'four-nets/icons/svg/icon_comments.svg';
import downloadSvg from 'four-nets/icons/svg/icon_bookmark.svg';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';

import CollectionImageSave from 'four-nets/photoblog/posts/common/feed-images/description/collection-image-save/collection-image-save';
import Comments from 'four-nets/photoblog/posts/common/comments/comments';

import ImageTagSlugs from '../tag-slugs/image-tag-slugs';
import ImageLikesButtons from '../buttons/image-likes-buttons/image-likes-buttons';

import Button from './button/button';
import { DescriptionUiFragment } from './ui.graphql';
import { PostType } from './ui.graphql.types';
import defaultStyles from './ui.scss';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';

export interface Props extends DescriptionUiFragment {
    styles: typeof defaultStyles;
    imageId?: string | null;
    commentId?: string | null;
}

enum Mode {
    COMMENTS = 'COMMENTS',
    COLLECTIONS = 'COLLECTIONS',
}

interface State {
    className?: string;
    showingText: boolean;
    showingModal: boolean;
    closingModal: boolean;
    commentId?: string;
    mode?: Mode;
}

class DescriptionUi extends React.PureComponent<Props, State> {
    static getDerivedStateFromProps: React.GetDerivedStateFromProps<Props, State> = (props, state) => {
        const { commentId } = state;
        if (props.imageId && props.commentId && props.commentId !== commentId) {
            return {
                showingModal: true,
                closingModal: false,
                commentId: props.commentId,
            };
        }
        return state;
    };

    state: State = {
        className: this.props.styles.content,
        showingText: false,
        showingModal: false,
        closingModal: true,
    };

    toggleMore = () => {
        this.setState(prevState => {
            const showingText = !prevState.showingText;

            const classNames = [this.props.styles.content];

            if (showingText) {
                classNames.push(this.props.styles.showingText);
            }

            return {
                className: classNames.join(' '),
                showingText,
            };
        });
    };

    label = (msg: React.ReactNode) => <span className={this.props.styles.label}>{msg}</span>;

    openModal = () => {
        this.setState({
            showingModal: true,
            closingModal: false,
        });
    };

    hideModal = () => {
        this.setState({
            closingModal: true,
        });
    };

    closeModal = () => {
        this.setState({
            showingModal: false,
        });
    };

    onCommentClick = (event: React.SyntheticEvent) => {
        this.stopPropagation(event);
        this.setState({ mode: 'COMMENTS' }, () => this.openModal());
    };

    onCollectionsClick = (event: React.SyntheticEvent) => {
        this.stopPropagation(event);
        this.setState({ mode: 'COLLECTIONS' }, () => this.openModal());
    };

    stopPropagation = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
    };

    render() {
        const { styles, commentId, ...image } = this.props;
        const { inViewerCollections, title, commentCount, post, id } = image;

        const postTitle = (post.postType === 'POST_ALBUM' && post.title) || null;

        const { showingText } = this.state;

        return (
            <EventBarrier className={this.state.className}>
                <TextShortener text={title || ''} words={showingText ? undefined : 10}>
                    {text => (
                        <React.Fragment>
                            <div className={styles.text}>
                                {postTitle ? <h2 className={styles.title}>{postTitle}</h2> : null}
                                <p className={styles.description}>{text}</p>
                            </div>
                            <div className={styles.line}>
                                <button className={styles.more} onClick={this.toggleMore} onTouchEnd={this.toggleMore}>
                                    {!showingText ? (
                                        <FormattedMessage id='BUTTON_SHOW_MORE' />
                                    ) : (
                                        <FormattedMessage id='BUTTON_SHOW_LESS' />
                                    )}
                                </button>
                            </div>
                        </React.Fragment>
                    )}
                </TextShortener>
                {showingText ? <ImageTagSlugs id={id} /> : null}
                <hr className={styles.separator} />
                <div className={styles.buttons}>
                    <div className={styles.likersButtonWrapper}>
                        <ImageLikesButtons style={{ likersButton: styles.button }} {...image} inUi={true} />
                    </div>
                    <Button src={commentsSvg} onClick={this.onCommentClick} onTouchEnd={this.onCommentClick}>
                        {commentCount ? (
                            <FormattedMessage id='%(counter)s_COMMENTS' values={{ counter: commentCount }}>
                                {this.label}
                            </FormattedMessage>
                        ) : (
                            <FormattedMessage id='BUTTON_COMMENT'>{this.label}</FormattedMessage>
                        )}
                    </Button>
                    <ViewerConsumer>
                        {({ viewer }) =>
                            viewer ? (
                                <Button
                                    src={downloadSvg}
                                    onClick={this.onCollectionsClick}
                                    onTouchEnd={this.onCollectionsClick}
                                >
                                    {inViewerCollections ? (
                                        <FormattedMessage id='SAVED' values={{ gender: 'v' }}>
                                            {this.label}
                                        </FormattedMessage>
                                    ) : (
                                        <FormattedMessage id='BUTTON_SAVE_IMAGE'>{this.label}</FormattedMessage>
                                    )}
                                </Button>
                            ) : null
                        }
                    </ViewerConsumer>
                    <Modal
                        style={{ modal: styles.modal }}
                        willClose={this.state.closingModal}
                        open={this.state.showingModal}
                    >
                        <Transition
                            timeout={{
                                enter: 0,
                                exit: 300,
                            }}
                            in={!this.state.closingModal}
                            appear={true}
                            mountOnEnter={true}
                            unmountOnExit={true}
                            onExited={this.closeModal}
                        >
                            {state => {
                                return (
                                    <React.Fragment>
                                        <div
                                            className={classnames(styles.backdrop, {
                                                [styles.backdropEntered]: state === ENTERED,
                                            })}
                                        />
                                        <EventBarrier
                                            className={classnames(styles.surface, {
                                                [styles.surfaceEntered]: state === ENTERED,
                                            })}
                                        >
                                            <div className={styles.modalContent}>
                                                <div className={styles.header}>
                                                    <h1 className={styles.headerTitle}>
                                                        {this.state.mode === Mode.COMMENTS ? (
                                                            <FormattedMessage id='COMMENTS' />
                                                        ) : this.state.mode === Mode.COLLECTIONS ? (
                                                            <FormattedMessage id='COLLECTIONS' />
                                                        ) : null}
                                                    </h1>
                                                    <FormattedMessage id='BUTTON_LABEL_CLOSE'>
                                                        {msg => (
                                                            <button
                                                                className={styles.headerClose}
                                                                title={msg as string}
                                                                onClick={this.hideModal}
                                                            >
                                                                <SVGIcon
                                                                    style={{ image: styles.headerIcon }}
                                                                    src={close}
                                                                />
                                                            </button>
                                                        )}
                                                    </FormattedMessage>
                                                </div>
                                                {this.state.mode === Mode.COMMENTS ? (
                                                    <Comments
                                                        variables={{ parentId: id }}
                                                        commentId={commentId || undefined}
                                                        scrollOnNew={false}
                                                    />
                                                ) : this.state.mode === Mode.COLLECTIONS ? (
                                                    <CollectionImageSave imageId={id} onClose={this.hideModal} />
                                                ) : null}
                                            </div>
                                        </EventBarrier>
                                    </React.Fragment>
                                );
                            }}
                        </Transition>
                    </Modal>
                </div>
            </EventBarrier>
        );
    }
}

export default Styled(defaultStyles)(DescriptionUi);
